'use client';

import { createRevealObserver } from '@/lib/reveal-observer';
import React, { useEffect, useRef } from 'react';

const philosophyPoints = [
  { label: 'Preparation', desc: 'The best investor conversations start before the first meeting.' },
  {
    label: 'Positioning',
    desc: 'How a company is framed determines how investors perceive the opportunity.',
  },
  { label: 'Investor Fit', desc: 'The right investor for this stage, sector and ambition level.' },
  {
    label: 'Relationships',
    desc: 'A fundraising round may last months. A strong investor relationship can last decades.',
  },
  { label: 'Timing', desc: 'Knowing when to raise is as important as knowing how to raise.' },
  {
    label: 'Execution',
    desc: 'Strategy means nothing without disciplined execution through the process.',
  },
];

export default function AboutStory() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = createRevealObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.scroll-fade').forEach((el) => {
              el.classList.add('visible');
            });
          }
        });
      },
      { rootMargin: '0px 0px -60px 0px', threshold: 0.05 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <>
      {/* Our Story */}
      <section ref={sectionRef} className="py-20 px-6 border-t border-primary/10">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-20">
            <div>
              <p className="section-label scroll-fade mb-5">Our Story</p>
              <h2 className="text-section-title font-sans font-semibold text-foreground scroll-fade scroll-fade-delay-1 mb-6">
                Our work sits at the intersection of founders, investors and{' '}
                <span className="font-serif italic text-primary">capital markets.</span>
              </h2>
            </div>
            <div className="space-y-5 scroll-fade scroll-fade-delay-2">
              <p className="text-base text-muted-foreground font-light leading-relaxed">
                Over the years, our team and network have worked across startup ecosystems,
                accelerator platforms, founder programs, capital raising, investor relationships,
                syndicates and fund structures.
              </p>
              <p className="text-base text-muted-foreground font-light leading-relaxed">
                We have worked with founders at different stages of their journey — from companies
                preparing for their first institutional round to businesses seeking significant
                growth capital.
              </p>
              <p className="text-base text-foreground font-medium leading-relaxed">
                That experience has shown us that fundraising success depends on much more than a
                pitch deck.
              </p>
            </div>
          </div>

          {/* What it depends on */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-16">
            {philosophyPoints?.map((point, i) => (
              <div
                key={point?.label}
                className={`bento-card rounded-2xl p-6 scroll-fade`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="gold-rule mb-4" />
                <h3 className="text-sm font-semibold text-foreground mb-2">{point?.label}</h3>
                <p className="text-xs text-muted-foreground font-light leading-relaxed">
                  {point?.desc}
                </p>
              </div>
            ))}
          </div>

          {/* History */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start border-t border-primary/10 pt-16">
            <div>
              <p className="section-label scroll-fade mb-5">Our History</p>
              <h2 className="text-2xl sm:text-3xl font-sans font-semibold text-foreground scroll-fade scroll-fade-delay-1 mb-6 leading-tight">
                Built through experience across the startup and{' '}
                <span className="font-serif italic text-primary">investment ecosystem.</span>
              </h2>
            </div>
            <div className="space-y-4 scroll-fade scroll-fade-delay-2">
              <p className="text-sm text-muted-foreground font-light leading-relaxed">
                Our journey has included working alongside accelerator ecosystems, supporting
                expansion and startup programs, building relationships with founders and investors,
                and participating in capital-raising activities across markets.
              </p>
              <p className="text-sm text-muted-foreground font-light leading-relaxed">
                Through this work, we have developed relationships with investors, funds, family
                offices, angels, syndicates and strategic capital providers.
              </p>
              <p className="text-sm text-foreground font-medium leading-relaxed">
                Aregn Capital brings these experiences into a dedicated capital-raising platform.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
